import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBrands } from "../contexts/BrandContext";
import { toast } from "sonner";
import { formatDateFr } from "../lib/dateFormat";
import {
  Building2, Check, X, Search, RefreshCw, Inbox, PackageMinus, PackagePlus,
  Trash2, MapPin, UserCog, Hourglass, PackageCheck, ShieldAlert,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import PurgeSiteHistoryDialog from "./PurgeSiteHistoryDialog";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import SignaturePad from "./SignaturePad";

const STATUS_BADGE = {
  en_attente: { label: "En attente", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approuve:   { label: "Approuvée", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  refuse:     { label: "Refusée",   cls: "bg-red-50 text-red-700 border-red-200" },
};

const REASON_LABEL = { usure: "Usure", defaut: "Défaut", perte: "Perte", autre: "Autre" };

/**
 * Onglet "Demandes sites" — visible admin/manager. Liste les demandes de matériel
 * pour un site émises par les collaborateurs et permet de :
 *  - approuver (signature contrôleur + choix du matériel catalogue + choix du receiver)
 *  - refuser (note motif obligatoire)
 *  - supprimer (admin only, en_attente uniquement)
 */
export default function EquipmentSiteRequestsTab() {
  const { user } = useAuth();
  const { selectedBrandId } = useBrands();
  const [requests, setRequests] = useState([]);
  const [stock, setStock] = useState([]);
  const [siteCollabs, setSiteCollabs] = useState([]); // collabs ayant un shift à venir sur le site sélectionné
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("en_attente");
  const [search, setSearch] = useState("");
  const [deletingId, setDeletingId] = useState("");

  const [decisionReq, setDecisionReq] = useState(null);
  const [decisionMode, setDecisionMode] = useState("approuve");
  const [decisionForm, setDecisionForm] = useState({
    controller_signature: null,
    equipment_id: "",
    quantite_validee: 1,
    decision_note: "",
    receiver_id: "",
  });

  // Finalisation d'une demande approuvée en `stock_pending`
  const [finalizeReq, setFinalizeReq] = useState(null);
  const [finalizeForm, setFinalizeForm] = useState({
    mode: "presence_sur_site",
    controller_signature: null,
    equipment_id: "",
    quantite_validee: 1,
    etat_constate: "Bon état",
    note: "",
    receiver_id: "",
    decision_note: "",
  });

  const isAdmin = user?.role === "admin";
  const canDecide = isAdmin || user?.role === "manager";
  const [purgeOpen, setPurgeOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Admin/Manager voient TOUTES les demandes sites (tous départements),
      // afin de ne pas rater les demandes émises par les collaborateurs sous
      // une autre marque que celle actuellement sélectionnée dans le picker.
      // Le scope d'accès reste géré côté backend (brand_ids du manager).
      const params = {};
      if (!canDecide && selectedBrandId && selectedBrandId !== "all") {
        params.brand_id = selectedBrandId;
      }
      const stockParams = selectedBrandId && selectedBrandId !== "all"
        ? { brand_id: selectedBrandId }
        : {};
      const [r, eq] = await Promise.all([
        api.get("/equipment-site-requests", { params }),
        api.get("/equipment", { params: stockParams }),
      ]);
      setRequests(r.data || []);
      setStock(eq.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement des demandes site");
    } finally {
      setLoading(false);
    }
  }, [selectedBrandId, canDecide]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const byStatus = statusFilter === "all"
      ? requests
      : requests.filter((r) => r.status === statusFilter);
    if (!term) return byStatus;
    return byStatus.filter((r) => {
      const haystack = [
        r.site_name, r.requester_name, r.designation_snapshot, r.ref_id_snapshot,
        r.besoin_titre, r.besoin_description, r.decision_note,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(term);
    });
  }, [requests, search, statusFilter]);

  const counts = useMemo(() => {
    const c = { en_attente: 0, approuve: 0, refuse: 0, all: requests.length };
    for (const r of requests) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [requests]);

  const openDecision = async (req, mode) => {
    setDecisionReq(req);
    setDecisionMode(mode);
    setDecisionForm({
      controller_signature: null,
      equipment_id: req.equipment_id || "",
      quantite_validee: req.quantite_demandee || 1,
      decision_note: "",
      receiver_id: req.requester_id,
    });
    // Charger les collabs ayant un shift sur le site dans les 7j (pour le receiver)
    try {
      const r = await api.get(`/sites/${req.site_id}/upcoming-collabs`);
      setSiteCollabs(r.data || []);
    } catch {
      // fallback : juste le demandeur
      setSiteCollabs([{ id: req.requester_id, name: req.requester_name }]);
    }
  };

  const submitDecision = async () => {
    if (!decisionReq) return;
    const payload = {
      status: decisionMode,
      decision_note: decisionForm.decision_note || "",
    };
    if (decisionMode === "approuve") {
      if (!decisionForm.controller_signature) {
        toast.error("Signature obligatoire pour valider");
        return;
      }
      if (!decisionForm.equipment_id) {
        toast.error("Sélectionnez le matériel à affecter au site");
        return;
      }
      payload.controller_signature = decisionForm.controller_signature;
      payload.equipment_id = decisionForm.equipment_id;
      payload.quantite_validee = Number(decisionForm.quantite_validee) || 1;
      payload.receiver_id = decisionForm.receiver_id || decisionReq.requester_id;
    } else if (!decisionForm.decision_note.trim()) {
      toast.error("Indiquez le motif du refus");
      return;
    }
    try {
      await api.put(`/equipment-site-requests/${decisionReq.id}/decision`, payload);
      toast.success(decisionMode === "approuve" ? "Demande approuvée — réception sur site à confirmer" : "Demande refusée");
      setDecisionReq(null);
      load();
      window.dispatchEvent(new CustomEvent("plania:equipment-site-requests-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la décision");
    }
  };

  const openFinalize = async (req) => {
    setFinalizeReq(req);
    setFinalizeForm({
      mode: "presence_sur_site",
      controller_signature: null,
      equipment_id: req.pending_equipment_id || req.equipment_id || "",
      quantite_validee: req.quantite_validee || req.quantite_demandee || 1,
      etat_constate: "Bon état",
      note: "",
      receiver_id: "",
      decision_note: "",
    });
    try {
      const r = await api.get(`/sites/${req.site_id}/upcoming-collabs`);
      setSiteCollabs(r.data || []);
    } catch {
      setSiteCollabs([]);
    }
  };

  const submitFinalize = async () => {
    if (!finalizeReq) return;
    if (!finalizeForm.controller_signature) {
      toast.error("Signature contrôleur obligatoire");
      return;
    }
    if (!finalizeForm.equipment_id) {
      toast.error("Sélectionnez le matériel à affecter");
      return;
    }
    if (finalizeForm.mode === "reception_par_collab" && !finalizeForm.receiver_id) {
      toast.error("Sélectionnez le collaborateur destinataire");
      return;
    }
    const payload = {
      mode: finalizeForm.mode,
      controller_signature: finalizeForm.controller_signature,
      equipment_id: finalizeForm.equipment_id,
      quantite_validee: Number(finalizeForm.quantite_validee) || 1,
      decision_note: finalizeForm.decision_note || "",
    };
    if (finalizeForm.mode === "presence_sur_site") {
      payload.etat_constate = finalizeForm.etat_constate || "Bon état";
      payload.note = finalizeForm.note || "";
    } else {
      payload.receiver_id = finalizeForm.receiver_id;
    }
    try {
      await api.post(`/equipment-site-requests/${finalizeReq.id}/finalize-stock`, payload);
      toast.success(
        finalizeForm.mode === "presence_sur_site"
          ? "Affectation livrée et confirmée sur site"
          : "Affectation finalisée — en attente de réception par le collaborateur",
      );
      setFinalizeReq(null);
      load();
      window.dispatchEvent(new CustomEvent("plania:equipment-site-requests-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la finalisation");
    }
  };

  const deleteRequest = async (r) => {
    if (!window.confirm(`Supprimer définitivement cette demande site ?\n\n• Demandeur : ${r.requester_name}\n• Site : ${r.site_name}\n\nIrréversible.`)) return;
    try {
      setDeletingId(r.id);
      await api.delete(`/equipment-site-requests/${r.id}`);
      toast.success("Demande supprimée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setDeletingId("");
    }
  };

  return (
    <div className="space-y-4" data-testid="equip-site-requests-tab">
      {isAdmin && (
        <PurgeSiteHistoryDialog
          open={purgeOpen}
          onOpenChange={setPurgeOpen}
          onDone={() => load()}
        />
      )}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg sm:text-xl font-display font-semibold text-zinc-900 flex items-center gap-2">
            <Building2 size={18} className="text-zinc-600" /> Demandes pour un site
          </h2>
          <p className="text-xs text-zinc-600 mt-1 font-mono">
            {counts.en_attente} en attente · {counts.approuve} approuvée(s) · {counts.refuse} refusée(s)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={load} disabled={loading}
            data-testid="equip-site-req-refresh"
          >
            <RefreshCw size={13} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Rafraîchir
          </Button>
          {isAdmin && (
            <Button
              variant="outline" size="sm"
              onClick={() => setPurgeOpen(true)}
              className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
              data-testid="equip-site-req-purge-btn"
              title="Supprimer les demandes et affectations historisées"
            >
              <ShieldAlert size={13} className="mr-1.5" /> Purger l&apos;historique
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-1.5" role="tablist" aria-label="Filtre par statut">
          {[
            { key: "en_attente", label: "En attente", cls: "border-amber-200 text-amber-800 bg-amber-50", activeCls: "bg-amber-600 text-white border-amber-700" },
            { key: "approuve", label: "Approuvées", cls: "border-emerald-200 text-emerald-800 bg-emerald-50", activeCls: "bg-emerald-600 text-white border-emerald-700" },
            { key: "refuse", label: "Refusées", cls: "border-red-200 text-red-800 bg-red-50", activeCls: "bg-red-600 text-white border-red-700" },
            { key: "all", label: "Toutes", cls: "border-zinc-200 text-zinc-700 bg-zinc-50", activeCls: "bg-zinc-900 text-white border-zinc-900" },
          ].map((t) => {
            const active = statusFilter === t.key;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setStatusFilter(t.key)}
                data-testid={`equip-site-req-tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border rounded-sm transition ${active ? t.activeCls : `${t.cls} hover:brightness-95`}`}
              >
                {t.label}
                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${active ? "bg-white/20 border-white/30 text-white" : "bg-white border-zinc-200 text-zinc-700"}`}>
                  {counts[t.key] ?? 0}
                </span>
              </button>
            );
          })}
        </div>
        <div>
          <Label className="label-uppercase mb-1.5 block inline-flex items-center gap-1.5">
            <Search size={10} /> Recherche
          </Label>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher (demandeur, site, matériel, motif…)"
            data-testid="equip-site-req-search"
          />
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm">
        {loading && filtered.length === 0 ? (
          <p className="text-sm text-zinc-500 font-mono py-10 text-center">Chargement…</p>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center" data-testid="equip-site-req-empty">
            <Inbox className="mx-auto text-zinc-300 mb-3" size={36} />
            <p className="text-sm text-zinc-500 font-mono">Aucune demande site dans ce filtre.</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {filtered.map((r) => {
              const b = STATUS_BADGE[r.status] || STATUS_BADGE.en_attente;
              const isRempl = r.request_type === "remplacement";
              return (
                <div key={r.id} className="p-4 flex items-start gap-3 flex-wrap" data-testid={`equip-site-req-row-${r.id}`}>
                  <span className="flex-shrink-0 mt-0.5">
                    {isRempl ? <PackageMinus size={18} className="text-amber-600" /> : <PackagePlus size={18} className="text-sky-600" />}
                  </span>
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider bg-zinc-50 border-zinc-200 text-zinc-700">
                        Site · {isRempl ? "Remplacement" : "Nouveau besoin"}
                      </span>
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider ${b.cls}`}>
                        {r.status === "en_attente" && <Hourglass size={10} />}
                        {b.label}
                      </span>
                      {r.status === "approuve" && r.stock_pending && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider bg-amber-50 border-amber-300 text-amber-800"
                          title="Approbation administrative enregistrée, mais aucune affectation n'a été créée car le stock est insuffisant."
                          data-testid={`equip-site-req-stock-pending-${r.id}`}
                        >
                          <Hourglass size={10} /> Stock en attente
                        </span>
                      )}
                      <p className="text-sm font-medium text-zinc-900 inline-flex items-center gap-1">
                        <MapPin size={12} className="text-zinc-400" />{r.site_name || "—"}
                      </p>
                    </div>
                    <p className="text-sm text-zinc-700">
                      Demandeur : <b>{r.requester_name}</b> ·
                      {isRempl
                        ? <> remplacement de <b>{r.designation_snapshot || "—"}</b> ({r.ref_id_snapshot || "—"}) ×&nbsp;<b>{r.quantite_demandee}</b></>
                        : <> {r.besoin_titre} ×&nbsp;<b>{r.quantite_demandee}</b></>}
                    </p>
                    {isRempl && r.reason_type && (
                      <p className="text-xs text-zinc-500 font-mono mt-0.5">
                        Motif : {REASON_LABEL[r.reason_type] || r.reason_type}{r.reason ? ` — ${r.reason}` : ""}
                      </p>
                    )}
                    {!isRempl && r.besoin_description && (
                      <p className="text-xs text-zinc-500 font-mono mt-0.5">{r.besoin_description}</p>
                    )}
                    {!isRempl && r.suggestion_url && (
                      <a href={r.suggestion_url} target="_blank" rel="noreferrer" className="text-xs text-sky-600 font-mono underline mt-0.5 inline-block">
                        Lien suggestion : {r.suggestion_url.slice(0, 60)}…
                      </a>
                    )}
                    <p className="text-[11px] text-zinc-400 font-mono mt-1">
                      Émise le {formatDateFr(r.created_at.slice(0, 10))}
                      {r.decided_at && r.status !== "en_attente" && (
                        <> · Décidée le {formatDateFr(r.decided_at.slice(0, 10))} par {r.decided_by_name}</>
                      )}
                    </p>
                    {r.status === "approuve" && r.receiver_name && (
                      <p className="text-xs text-sky-700 mt-1 inline-flex items-center gap-1">
                        <PackageCheck size={11} /> Réception sur site confiée à <b>{r.receiver_name}</b>
                      </p>
                    )}
                    {r.status === "approuve" && r.stock_pending && (
                      <p className="text-xs text-amber-800 mt-1 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1">
                        <b>Stock insuffisant à l'approbation.</b> Aucune affectation n'a été générée — à finaliser ultérieurement (réappro / autre matériel équivalent).
                      </p>
                    )}
                    {r.status === "refuse" && r.decision_note && (
                      <p className="text-xs text-red-700 mt-1">Motif refus : {r.decision_note}</p>
                    )}
                  </div>
                  {canDecide && r.status === "en_attente" && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        size="sm" onClick={() => openDecision(r, "approuve")}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`equip-site-req-approve-${r.id}`}
                      >
                        <Check size={13} className="mr-1.5" /> Valider
                      </Button>
                      <Button
                        variant="outline" size="sm" onClick={() => openDecision(r, "refuse")}
                        className="border-red-200 text-red-700 hover:bg-red-50"
                        data-testid={`equip-site-req-reject-${r.id}`}
                      >
                        <X size={13} className="mr-1.5" /> Refuser
                      </Button>
                    </div>
                  )}
                  {canDecide && r.status === "approuve" && r.stock_pending && (
                    <Button
                      size="sm" onClick={() => openFinalize(r)}
                      className="bg-amber-600 hover:bg-amber-700"
                      data-testid={`equip-site-req-finalize-${r.id}`}
                    >
                      <PackageCheck size={13} className="mr-1.5" /> Finaliser l'affectation
                    </Button>
                  )}
                  {isAdmin && r.status === "en_attente" && (
                    <Button
                      variant="outline" size="sm" onClick={() => deleteRequest(r)}
                      disabled={deletingId === r.id}
                      className="h-8 px-2 text-red-700 border-red-200 hover:bg-red-50"
                      data-testid={`equip-site-req-delete-${r.id}`}
                      title="Supprimer définitivement (admin)"
                    >
                      <Trash2 size={13} />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <DecisionDialog
        decisionReq={decisionReq}
        decisionMode={decisionMode}
        decisionForm={decisionForm}
        setDecisionForm={setDecisionForm}
        stock={stock}
        siteCollabs={siteCollabs}
        onClose={() => setDecisionReq(null)}
        onSubmit={submitDecision}
      />

      <FinalizeDialog
        finalizeReq={finalizeReq}
        finalizeForm={finalizeForm}
        setFinalizeForm={setFinalizeForm}
        stock={stock}
        siteCollabs={siteCollabs}
        onClose={() => setFinalizeReq(null)}
        onSubmit={submitFinalize}
      />
    </div>
  );
}

function DecisionDialog({ decisionReq, decisionMode, decisionForm, setDecisionForm, stock, siteCollabs, onClose, onSubmit }) {
  if (!decisionReq) return null;
  const isApprove = decisionMode === "approuve";
  const isRempl = decisionReq.request_type === "remplacement";
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="equip-site-req-dialog">
        <DialogHeader>
          <DialogTitle>
            {isApprove ? "Valider" : "Refuser"} la demande site — {decisionReq.site_name}
          </DialogTitle>
          <DialogDescription>
            Demandeur : <b>{decisionReq.requester_name}</b> — {isRempl ? "Remplacement" : "Nouveau besoin"} :{" "}
            {isRempl ? decisionReq.designation_snapshot : decisionReq.besoin_titre}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {isApprove && (
            <>
              <div>
                <Label className="label-uppercase">Matériel concret à affecter au site</Label>
                <Select
                  value={decisionForm.equipment_id}
                  onValueChange={(v) => setDecisionForm({ ...decisionForm, equipment_id: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="equip-site-req-equipment-select">
                    <SelectValue placeholder="Choisissez dans le catalogue" />
                  </SelectTrigger>
                  <SelectContent>
                    {stock.map((e) => (
                      <SelectItem key={e.id} value={e.id} data-testid={`equip-site-req-eq-${e.id}`}>
                        {e.designation} ({e.ref_id}) — stock {e.quantite}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-uppercase">Quantité validée</Label>
                  <Input
                    type="number" min={1} value={decisionForm.quantite_validee}
                    onChange={(e) => setDecisionForm({ ...decisionForm, quantite_validee: e.target.value })}
                    className="mt-1"
                    data-testid="equip-site-req-qty"
                  />
                </div>
                <div>
                  <Label className="label-uppercase">Collaborateur réceptionnaire</Label>
                  <Select
                    value={decisionForm.receiver_id}
                    onValueChange={(v) => setDecisionForm({ ...decisionForm, receiver_id: v })}
                  >
                    <SelectTrigger className="mt-1" data-testid="equip-site-req-receiver">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={decisionReq.requester_id} data-testid={`equip-site-req-receiver-${decisionReq.requester_id}`}>
                        {decisionReq.requester_name} (demandeur)
                      </SelectItem>
                      {siteCollabs
                        .filter((c) => c.id !== decisionReq.requester_id)
                        .map((c) => (
                          <SelectItem key={c.id} value={c.id} data-testid={`equip-site-req-receiver-${c.id}`}>
                            {c.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="label-uppercase flex items-center gap-1">
                  <UserCog size={11} /> Votre signature contrôleur
                </Label>
                <SignaturePad
                  value={decisionForm.controller_signature}
                  onSave={(s) => setDecisionForm({ ...decisionForm, controller_signature: s })}
                />
              </div>
            </>
          )}
          <div>
            <Label className="label-uppercase">
              {isApprove ? "Note (optionnel)" : "Motif du refus"}
            </Label>
            <Textarea
              value={decisionForm.decision_note}
              onChange={(e) => setDecisionForm({ ...decisionForm, decision_note: e.target.value })}
              rows={2} className="mt-1"
              placeholder={isApprove ? "Précisions optionnelles" : "Indiquez le motif de refus"}
              data-testid="equip-site-req-note"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            onClick={onSubmit}
            className={isApprove ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
            data-testid="equip-site-req-submit"
          >
            {isApprove ? "Valider la demande" : "Refuser la demande"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const ETATS_RECEPTION = ["Neuf", "Bon état", "État d'usage", "Endommagé"];

function FinalizeDialog({ finalizeReq, finalizeForm, setFinalizeForm, stock, siteCollabs, onClose, onSubmit }) {
  if (!finalizeReq) return null;
  const isRempl = finalizeReq.request_type === "remplacement";
  const isPresence = finalizeForm.mode === "presence_sur_site";
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="equip-site-req-finalize-dialog">
        <DialogHeader>
          <DialogTitle>Finaliser l'affectation — {finalizeReq.site_name}</DialogTitle>
          <DialogDescription>
            Demande approuvée en attente de stock. Demandeur : <b>{finalizeReq.requester_name}</b> —{" "}
            {isRempl ? "Remplacement" : "Nouveau besoin"} :{" "}
            {isRempl ? finalizeReq.designation_snapshot : finalizeReq.besoin_titre} ×&nbsp;
            <b>{finalizeReq.quantite_demandee}</b>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="label-uppercase mb-2 block">Mode de livraison</Label>
            <RadioGroup
              value={finalizeForm.mode}
              onValueChange={(v) => setFinalizeForm({ ...finalizeForm, mode: v })}
              className="grid grid-cols-1 sm:grid-cols-2 gap-2"
            >
              <label
                htmlFor="finalize-mode-presence"
                className={`border rounded-sm p-3 cursor-pointer flex items-start gap-2 transition ${isPresence ? "border-amber-400 bg-amber-50" : "border-zinc-200 hover:bg-zinc-50"}`}
                data-testid="equip-site-req-finalize-mode-presence-label"
              >
                <RadioGroupItem value="presence_sur_site" id="finalize-mode-presence" data-testid="equip-site-req-finalize-mode-presence" />
                <div className="text-xs">
                  <p className="font-semibold text-zinc-900">Immédiatement sur site</p>
                  <p className="text-zinc-600 mt-0.5">Vous êtes sur place : vous livrez et confirmez immédiatement.</p>
                </div>
              </label>
              <label
                htmlFor="finalize-mode-collab"
                className={`border rounded-sm p-3 cursor-pointer flex items-start gap-2 transition ${!isPresence ? "border-sky-400 bg-sky-50" : "border-zinc-200 hover:bg-zinc-50"}`}
                data-testid="equip-site-req-finalize-mode-collab-label"
              >
                <RadioGroupItem value="reception_par_collab" id="finalize-mode-collab" data-testid="equip-site-req-finalize-mode-collab" />
                <div className="text-xs">
                  <p className="font-semibold text-zinc-900">À réceptionner sur site</p>
                  <p className="text-zinc-600 mt-0.5">Un collaborateur planifié sur le site confirmera la réception.</p>
                </div>
              </label>
            </RadioGroup>
          </div>

          <div>
            <Label className="label-uppercase">Matériel à affecter</Label>
            <Select
              value={finalizeForm.equipment_id}
              onValueChange={(v) => setFinalizeForm({ ...finalizeForm, equipment_id: v })}
            >
              <SelectTrigger className="mt-1" data-testid="equip-site-req-finalize-equipment">
                <SelectValue placeholder="Choisissez dans le catalogue" />
              </SelectTrigger>
              <SelectContent>
                {stock.map((e) => (
                  <SelectItem key={e.id} value={e.id} data-testid={`equip-site-req-finalize-eq-${e.id}`}>
                    {e.designation} ({e.ref_id}) — stock {e.quantite}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-uppercase">Quantité validée</Label>
              <Input
                type="number" min={1} value={finalizeForm.quantite_validee}
                onChange={(e) => setFinalizeForm({ ...finalizeForm, quantite_validee: e.target.value })}
                className="mt-1"
                data-testid="equip-site-req-finalize-qty"
              />
            </div>
            {isPresence ? (
              <div>
                <Label className="label-uppercase">État constaté</Label>
                <Select
                  value={finalizeForm.etat_constate}
                  onValueChange={(v) => setFinalizeForm({ ...finalizeForm, etat_constate: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="equip-site-req-finalize-etat">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ETATS_RECEPTION.map((e) => (
                      <SelectItem key={e} value={e} data-testid={`equip-site-req-finalize-etat-${e}`}>{e}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label className="label-uppercase">Collaborateur destinataire</Label>
                <Select
                  value={finalizeForm.receiver_id}
                  onValueChange={(v) => setFinalizeForm({ ...finalizeForm, receiver_id: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="equip-site-req-finalize-receiver">
                    <SelectValue placeholder={siteCollabs.length === 0 ? "Aucun shift planifié sur 7j" : "Choisissez un collaborateur"} />
                  </SelectTrigger>
                  <SelectContent>
                    {siteCollabs.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`equip-site-req-finalize-receiver-${c.id}`}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div>
            <Label className="label-uppercase flex items-center gap-1">
              <UserCog size={11} /> Votre signature contrôleur
            </Label>
            <SignaturePad
              value={finalizeForm.controller_signature}
              onSave={(s) => setFinalizeForm({ ...finalizeForm, controller_signature: s })}
            />
            {isPresence && (
              <p className="text-[11px] text-zinc-500 font-mono mt-1">
                En mode « Immédiatement sur site », votre signature vaut également confirmation de réception.
              </p>
            )}
          </div>

          {isPresence && (
            <div>
              <Label className="label-uppercase">Note de réception (optionnel)</Label>
              <Textarea
                value={finalizeForm.note}
                onChange={(e) => setFinalizeForm({ ...finalizeForm, note: e.target.value })}
                rows={2} className="mt-1"
                placeholder="Observations sur la livraison / l'état du matériel"
                data-testid="equip-site-req-finalize-note"
              />
            </div>
          )}

          <div>
            <Label className="label-uppercase">Commentaire de finalisation (optionnel)</Label>
            <Textarea
              value={finalizeForm.decision_note}
              onChange={(e) => setFinalizeForm({ ...finalizeForm, decision_note: e.target.value })}
              rows={2} className="mt-1"
              placeholder="Précisions à conserver dans l'historique de la demande"
              data-testid="equip-site-req-finalize-decision-note"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            onClick={onSubmit}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="equip-site-req-finalize-submit"
          >
            <PackageCheck size={13} className="mr-1.5" />
            {isPresence ? "Livrer et confirmer" : "Finaliser et notifier le collaborateur"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
